import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VoyahCourageExpEditor = () => {
  const [form, setForm] = useState({
    titleEn: "",
    titleAr: "",
    titleKu: "",
    paragraphEn: "",
    paragraphAr: "",
    paragraphKu: "",
    button1En: "",
    button1Ar: "",
    button1Ku: "",
    button2En: "",
    button2Ar: "",
    button2Ku: "",
  });

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  const LANGS = ["En", "Ar", "Ku"];

  // ============================================================
  // LOAD DATA (3 languages)
  // ============================================================
  useEffect(() => {
    const loadData = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VoyahCourageExpSection?lang=en`),
          axios.get(`${API_BASE}/api/VoyahCourageExpSection?lang=ar`),
          axios.get(`${API_BASE}/api/VoyahCourageExpSection?lang=ku`),
        ]);

        setForm({
          titleEn: en.data.title,
          paragraphEn: en.data.paragraph,
          button1En: en.data.button1,
          button2En: en.data.button2,

          titleAr: ar.data.title,
          paragraphAr: ar.data.paragraph,
          button1Ar: ar.data.button1,
          button2Ar: ar.data.button2,

          titleKu: ku.data.title,
          paragraphKu: ku.data.paragraph,
          button1Ku: ku.data.button1,
          button2Ku: ku.data.button2,
        });

        setImage(en.data.image);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load exp section", err);
      }
    };

    loadData();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ============================================================
  // SAVE TEXTS
  // ============================================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(`${API_BASE}/api/VoyahCourageExpSection`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Exp section updated!");
    } catch (err) {
      console.error(err);
      alert("Failed to save.");
    }
  };

  // ============================================================
  // DELETE IMAGE
  // ============================================================
  const handleDeleteImage = async () => {
    const token = localStorage.getItem("token");

    if (!image) return alert("No image to delete.");

    try {
      await axios.delete(`${API_BASE}/api/VoyahCourageExpSection/media`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { url: image },
      });

      alert("Image deleted!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // ============================================================
  // UPLOAD IMAGE
  // ============================================================
  const handleUploadImage = async () => {
    if (!uploadFile) return alert("Choose a file.");

    const token = localStorage.getItem("token");
    const fd = new FormData();
    fd.append("BackgroundImage", uploadFile);

    try {
      await axios.put(`${API_BASE}/api/VoyahCourageExpSection/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Image updated!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Voyah Courage – Experience Section Editor</h2>

      {/* ============================================================
          TEXT INPUTS (3 LANGUAGES)
      ============================================================ */}
      <div className="row">
        {LANGS.map((lang) => (
          <div className="col-lg-4 col-md-6 mb-4" key={lang}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h5 className="text-primary mb-3">{lang} version</h5>

                {/* Title */}
                <label className="form-label">Title</label>
                <input
                  className="form-control mb-2"
                  name={`title${lang}`}
                  value={form[`title${lang}`]}
                  onChange={handleChange}
                />

                {/* Paragraph */}
                <label className="form-label">Paragraph</label>
                <textarea
                  className="form-control mb-2"
                  rows={3}
                  name={`paragraph${lang}`}
                  value={form[`paragraph${lang}`]}
                  onChange={handleChange}
                />

                {/* Button 1 */}
                <label className="form-label">Button 1</label>
                <input
                  className="form-control mb-2"
                  name={`button1${lang}`}
                  value={form[`button1${lang}`]}
                  onChange={handleChange}
                />

                {/* Button 2 */}
                <label className="form-label">Button 2</label>
                <input
                  className="form-control"
                  name={`button2${lang}`}
                  value={form[`button2${lang}`]}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAVE BUTTON */}
      <div className="text-end mb-5">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save Changes
        </button>
      </div>

      {/* ============================================================
          IMAGE BLOCK
      ============================================================ */}
      <h4 className="mb-3">Background Image</h4>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          {image ? (
            <img
              src={resolveUrl(image)}
              alt=""
              className="w-100 rounded mb-3"
              style={{ maxHeight: "260px", objectFit: "cover" }}
            />
          ) : (
            <p>No image uploaded.</p>
          )}

          <div className="d-flex gap-2">
            <button
              className="btn btn-danger"
              disabled={!image}
              onClick={handleDeleteImage}
            >
              Delete Image
            </button>

            <button
              className="btn btn-primary"
              onClick={() => {
                setUploadFile(null);
                setShowModal(true);
              }}
            >
              Upload / Replace
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================
          MODAL FOR UPLOAD
      ============================================================ */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Background Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={handleUploadImage}
                  >
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VoyahCourageExpEditor;
